import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { parseDate, now, startOfMonth, endOfMonth, toFormDate } from '../../../config/dayjsConfig'
import { useBackend } from '../../../services/backend'
import { Dimension, DimensionQueryOperator } from '../../../features/dimension/types/Dimension'
import { currentUserSortedCompaniesSelector } from '../../../redux/session/currentUser/selectors'
import { Report, ReportDataType, ReportRow } from '../../../redux/context/reports/types'
import { getCompanysKeyFigureIds, mapToGlobalKeyFigureStatement } from '../../../utils/keyFigures'
import { Company } from '../../../types/company/Company'
import { Currency } from '../../../types/currency/Currency'
import { Formula } from '../../../features/formulas/types/Formula'
import { MultiDashboardFormFields } from '../../../features/dashboards/components/MultiDashboardForm'

import { CompanyTableRow } from './components/MultiPurposeDashboardTable'
import { chunkArray } from '../../../utils/helpers'
import { filtersSelector } from '../../../redux/context/filters/selectors'
import { getDashboardStartAndEndDates } from './utils'
import { multiPurposeDashboardPageSelector } from '../../../redux/pages/dashboard/multiPurposeDashboard/selectors'
import {
  addMultiPurposeDashboardDimensionKeyfigureStatements,
  addMultiPurposeDashboardDimensions,
  resetMultiPurposeDashboardReports,
  setMultiPurposeDashboardAllKeyfigureStatements,
  setMultiPurposeDashboardFormulas
} from '../../../redux/pages/dashboard/multiPurposeDashboard/actions'
import { contextCompanySelector } from '../../../redux/context/company/selectors'
import { AppDispatch } from '../../../redux/store'
import {
  MultiDashboard,
  MultiDashboardDeleteMutation,
  MultiDashboardUpdateMutation
} from '../../../features/dashboards'
import { DashboardPageType } from '../types'
import { useUpdateCompaniesDashboard } from '../../../features/dashboards/queries/useUpdateCompaniesDashboard'
import { useUpdateDimensionDashboard } from '../../../features/dashboards/queries/useUpdateDimensionDashboard'
import { useDeleteCompaniesDashboard } from '../../../features/dashboards/queries/useDeleteCompaniesDashboard'
import { useDeleteDimensionCompactDashboard } from '../../../features/dashboards/queries/useDeleteDimensionCompactDashboard'
import { useDeleteDimensionDefaultDashboard } from '../../../features/dashboards/queries/useDeleteDimensionDefaultDashboard'
import { useCompanyDimensionTree } from '../../../features/dimension/queries/useCompanyDimensionTree'
import { useMultipleCompanyFormulas } from '../../../features/formulas/queries/useMultipleCompanyFormulas'

export type MultiPurposeDashboardContextType = {
  loading: boolean
  dashboard?: MultiDashboard
  dimensionLoading: boolean
  loadingDimensionData?: boolean
  formSaveLoading?: boolean
  dashboardRef: React.MutableRefObject<MultiDashboard | undefined>
  expandedRowKeys: string[]
  multiPurposeDashboardPage?: DashboardPageType
  updateDashboard: (values: MultiDashboardFormFields) => void
  setFormulaCompanies: (companyIds: string[]) => void
  deleteDashboard: () => void
  handleTableExpand: (tableRow: CompanyTableRow) => void
  setExpandedRowKeys: (values: string[]) => void
}

export const MultiPurposeDashboardContext = React.createContext<MultiPurposeDashboardContextType | null>(null)

interface MultiPurposeDashboardProviderProps {
  multiPurposeDashboardPage: DashboardPageType
  dashboard?: MultiDashboard
  children?: ReactNode
}

const updateMutations: {
  [key in DashboardPageType]: MultiDashboardUpdateMutation
} = {
  [DashboardPageType.companies]: useUpdateCompaniesDashboard,
  [DashboardPageType.compact]: useUpdateDimensionDashboard,
  [DashboardPageType.default]: useUpdateDimensionDashboard
}

const deleteMutations: {
  [key in DashboardPageType]: MultiDashboardDeleteMutation
} = {
  [DashboardPageType.companies]: useDeleteCompaniesDashboard,
  [DashboardPageType.compact]: useDeleteDimensionCompactDashboard,
  [DashboardPageType.default]: useDeleteDimensionDefaultDashboard
}

const MultiPurposeDashboardProvider: React.FC<MultiPurposeDashboardProviderProps> = ({
  children,
  dashboard,
  multiPurposeDashboardPage
}) => {
  const dispatch: AppDispatch = useDispatch()

  const {
    keyfigureStatements,
    dimensionKeyfigureStatements,
    formulas: formulaReports,
    dimensions: dimensionsMap
  } = useSelector(multiPurposeDashboardPageSelector)
  const contextCompany = useSelector(contextCompanySelector)
  const dashboardRef = useRef<MultiDashboard>()
  const myCompanies = useSelector(currentUserSortedCompaniesSelector)

  const { periodGroups } = useSelector(filtersSelector)
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | undefined>(undefined)
  const { data: dimensionData = [], isLoading: dimensionsLoading } = useCompanyDimensionTree(selectedCompanyId)
  const keyFigureRequest = useBackend('/api/companies/{companyId}/reporting/custom')
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const [companyReportsLoading, setCompanyReportsLoading] = useState<boolean>(false)
  const [dimensionReportsLoading, setDimensionReportsLoading] = useState<boolean>(false)
  const [formFormulaCompanies, setFormulaCompanies] = useState<string[]>([])

  let formulaCompanies = (dashboard?.companies || []).filter(companyId => !(companyId in formulaReports))
  if (formulaCompanies?.length < 1 && contextCompany) {
    formulaCompanies = [contextCompany.id]
  }

  const mergedCompanies = [...new Set([...formulaCompanies, ...formFormulaCompanies])]
  const formulaResults = useMultipleCompanyFormulas(mergedCompanies)

  const formulasLoading = formulaResults.some(result => result.isLoading)
  const formulasError = formulaResults.some(result => result.isError)

  const { isPending: isPendingUpdate, mutateAsync: mutateAsyncUpdate } = updateMutations[multiPurposeDashboardPage]()
  const { mutateAsync: mutateAsyncDelete } = deleteMutations[multiPurposeDashboardPage]()

  useEffect(() => {
    if (!formulasLoading && !formulasError && formulaCompanies.length > 0) {
      const newData: {
        [k: string]: Formula[]
      } = {}
      formulaResults.forEach((result, index) => {
        if (result?.data && !formulaReports?.[formulaCompanies[index]]) {
          const companyId = formulaCompanies[index]
          newData[companyId] = result.data || []
        }
      })

      try {
        if (Object.entries(newData).length !== 0) {
          dispatch(setMultiPurposeDashboardFormulas(newData))
        }
      } catch (error) {
        console.error('Failed to dispatch formulas:', error)
      }
    }
  }, [formulaCompanies, formulaResults, formulasLoading, formulasError, formulaReports])

  useEffect(() => {
    if (!dimensionsMap[dimensionData?.[0]?.companyId]) {
      dispatch(addMultiPurposeDashboardDimensions(dimensionData?.[0]?.companyId, dimensionData))
    }
  }, [dimensionData])

  const getTableTree = () => {
    if (!dashboard || !contextCompany) return []
    const notFetchedDimensions: { [key: string]: Dimension[] } = {}

    // Comapnies map
    let mapCompanies = dashboard?.companies || []
    if (contextCompany && mapCompanies?.length < 1) {
      mapCompanies = [contextCompany.id]
    }
    mapCompanies
      ?.filter((id: string) => !!myCompanies.find(c => c.id === id))
      .map((id: string) => {
        const company = myCompanies.find(c => c.id === id)!
        const tableRow: any = {
          ...company,
          children: company ? dimensionsMap[company.id] : undefined
        }
        // Dimension map
        const map = ({ children: c, ...o }: any): any => {
          const fetchedKeyFigureStatementIds = dimensionKeyfigureStatements[o.id]?.map(row => row.id) || []
          const dashboardKeyFigureIds = [...(dashboard?.keyFigures || []), ...(dashboard.formulas || [])]
          const notFetchedKeyFigureIds = dashboardKeyFigureIds.filter(
            keyFigureIdsid => !fetchedKeyFigureStatementIds.includes(keyFigureIdsid)
          )
          // If there's  notFetchedKeyFigureIds or no report get report
          if (notFetchedKeyFigureIds?.length || !Object.keys(dimensionKeyfigureStatements).includes(o.id.toString())) {
            if (!notFetchedDimensions[company.id]) {
              notFetchedDimensions[company.id] = []
            }
            notFetchedDimensions[company.id].push(o)
          }

          return {
            company,
            ...o,
            children: expandedRowKeys.includes(`dimension-${o.id}`) ? c?.map(map) : undefined // recursive call
          }
        }

        return {
          ...tableRow,
          children: expandedRowKeys.includes(`company-${company.id}`) ? tableRow.children?.map(map) : undefined
        }
      })

    return notFetchedDimensions
  }

  const transformParams = (values: MultiDashboardFormFields): MultiDashboard => {
    const { period, ...rest } = values
    const [start, end] = period || []
    return {
      ...rest,
      ...(start &&
        end && {
          startDate: startOfMonth(start),
          endDate: endOfMonth(end)
        })
    }
  }

  const fetchCompanyReports = (company: Company, data: MultiDashboard, dimensionIds?: string[]): Promise<Report> => {
    const keyFigureIds = getCompanysKeyFigureIds(company, data?.keyFigures)
    const { startDate, endDate } = getDashboardStartAndEndDates(data, periodGroups, multiPurposeDashboardPage)

    if (
      multiPurposeDashboardPage === DashboardPageType.companies ||
      multiPurposeDashboardPage === DashboardPageType.compact
    ) {
      return keyFigureRequest.get({
        urlParams: { companyId: company.id },
        body: {
          params: {
            dimensions: JSON.stringify(
              dimensionIds?.map(id => ({
                k: id,
                o: DimensionQueryOperator.has,
                v: id
              }))
            ),
            keyFigureIds,
            formulaIds: data.formulas,
            periodGroups: [
              {
                id: (parseDate(startDate) || now()).year(),
                startDate: startDate || '',
                endDate: endDate || ''
              },
              {
                id: -(parseDate(startDate) || now()).year(),
                startDate: toFormDate(parseDate(startDate)?.subtract(12, 'M')) || '',
                endDate: toFormDate(parseDate(endDate)?.subtract(12, 'M')) || ''
              }
            ],
            currency: data.currency || Currency.EUR,
            dataTypes: [ReportDataType.actuals],
            ...(multiPurposeDashboardPage === DashboardPageType.companies && {
              budgetingScenarioId: undefined
            })
          }
        }
      })
    }
    return Promise.resolve([])
  }

  const updateCompanyReports = async (companies: string[], data: MultiDashboard) => {
    setCompanyReportsLoading(true)
    let mapCompanies = companies || []
    if (contextCompany && mapCompanies?.length < 1) {
      mapCompanies = [contextCompany.id]
    }
    async function fetchCompanyReportsChunk() {
      const promises = []
      for (let i = 0; i < mapCompanies.length; i += 150) {
        const companyIds = mapCompanies.slice(i, i + 150)
        const companyPromises = companyIds.map((companyId: string) => {
          const company = myCompanies.find(c => c.id === companyId)
          if (company) {
            return fetchCompanyReports(company, data)
              .then((res: any) => res)
              .catch(() => []) as Promise<any>
          }
          return Promise.resolve([])
        })

        // eslint-disable-next-line no-await-in-loop
        const results = await Promise.all(companyPromises)
        promises.push(...results)
      }
      return promises
    }
    const companyPromises = await fetchCompanyReportsChunk()
    const promises = companyPromises.reduce((arr: any, dimArr: any) => {
      arr.push(...dimArr)
      return arr
    }, [])

    if (promises) {
      const allData = await Promise.all(promises)
      const theData = mapCompanies?.reduce((acc, companyId) => {
        return {
          ...acc,
          [companyId]: mapToGlobalKeyFigureStatement(allData?.find(a => a.companyId === companyId)?.data || [])
        }
      }, {} as { [key: string]: ReportRow[] })
      theData && dispatch(setMultiPurposeDashboardAllKeyfigureStatements(theData))
    }
    setCompanyReportsLoading(false)
  }

  const fetchDimensionReports = async (dimensions: Dimension[], company: Company) => {
    if (!dashboard) return
    setDimensionReportsLoading(true)

    const fetchAllReports = async (fetchCompany: Company, selectedDashboard: MultiDashboard, chunk: Dimension[]) => {
      const promises = await fetchCompanyReports(
        fetchCompany,
        selectedDashboard,
        chunk.map(c => c.dimensionId)
      )
      // eslint-disable-next-line no-return-await
      return await Promise.all(promises)
    }

    const fetchAllDimensions = async (
      fetchCompany: Company,
      selectedDashboard: MultiDashboard,
      dimensionsArr: Dimension[]
    ) => {
      const chunkedDimensions = chunkArray(dimensionsArr, 150)
      const results = []
      for (const chunk of chunkedDimensions) {
        // eslint-disable-next-line no-await-in-loop
        const reports = await fetchAllReports(fetchCompany, selectedDashboard, chunk)
        results.push(...reports.map(r => r.data))
      }
      return results as unknown as Report[]
    }

    const reports = await fetchAllDimensions(company, dashboard, dimensions)

    dispatch(
      addMultiPurposeDashboardDimensionKeyfigureStatements(
        dimensions?.reduce((acc, { id }, i) => {
          return { ...acc, [id]: mapToGlobalKeyFigureStatement(reports[i]) }
        }, {} as { [key: string]: ReportRow[] })
      )
    )
    setDimensionReportsLoading(false)
  }

  const handleTableExpand = (tableRow: CompanyTableRow) => {
    if (!tableRow.dimensionId && !dimensionsMap[tableRow.id]) {
      setSelectedCompanyId(tableRow.id)
    }
  }

  const updateReports = (newParams: MultiDashboard) => {
    const updateAll =
      dashboardRef.current?.currency !== newParams.currency ||
      dashboardRef.current?.startDate !== newParams.startDate ||
      dashboardRef.current?.endDate !== newParams.endDate

    if (updateAll) dispatch(resetMultiPurposeDashboardReports())

    const filteredCompanies = newParams?.companies?.filter((companyId: string) => {
      if (updateAll) return true
      const company = myCompanies.find(c => c.id === companyId)
      const fetchedKeyFigureStatementIds = keyfigureStatements[companyId]?.map(row => row.id) || []
      const dashboardKeyFigureIds = [...(newParams?.keyFigures || []), ...(newParams.formulas || [])]
      const notFetchedKeyFigureIds = dashboardKeyFigureIds.filter(id => !fetchedKeyFigureStatementIds.includes(id))

      if (company && notFetchedKeyFigureIds.length) {
        return true
      }
      return false
    })

    updateCompanyReports(filteredCompanies || [], newParams)
  }

  const updateDashboard = async (values: MultiDashboardFormFields) => {
    if (!values.id) return

    try {
      const transformed = transformParams(values)
      await mutateAsyncUpdate({ data: transformed, id: values.id })
      updateReports(transformed)
      dashboardRef.current = transformed
    } catch (error) {
      console.error(error)
    }
  }

  const deleteDashboard = async () => {
    if (!dashboard?.id) return
    try {
      await mutateAsyncDelete(dashboard?.id)
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (expandedRowKeys.length > 0) {
      const notFetchedDimensions = getTableTree()
      Object.entries(notFetchedDimensions).forEach(([companyId, dimensions]) => {
        const company = myCompanies.find(c => c.id === companyId)
        company && fetchDimensionReports(dimensions, company)
      })
    }
  }, [expandedRowKeys, dimensionsMap, dashboardRef.current])

  useEffect(() => {
    if (dashboard) {
      if (
        multiPurposeDashboardPage === DashboardPageType.companies ||
        multiPurposeDashboardPage === DashboardPageType.compact
      ) {
        if ((!dashboardRef.current && dashboard) || dashboardRef.current?.id !== dashboard?.id) {
          dispatch(resetMultiPurposeDashboardReports())
          setExpandedRowKeys([])
          dashboardRef.current = dashboard
          updateCompanyReports(dashboard?.companies || [], dashboard)
        }
      }
    }
  }, [dashboard?.id, periodGroups])

  return (
    <MultiPurposeDashboardContext.Provider
      value={{
        loading: dimensionReportsLoading || companyReportsLoading || dimensionsLoading,
        dashboard,
        dashboardRef,
        loadingDimensionData: dimensionsLoading,
        formSaveLoading: isPendingUpdate,
        dimensionLoading: dimensionsLoading,
        expandedRowKeys,
        multiPurposeDashboardPage,
        updateDashboard,
        setFormulaCompanies,
        deleteDashboard,
        handleTableExpand,
        setExpandedRowKeys
      }}
    >
      {children}
    </MultiPurposeDashboardContext.Provider>
  )
}

export default MultiPurposeDashboardProvider
